import { toUtf8 } from "@cosmjs/encoding";
import { MsgGrantAllowance } from "cosmjs-types/cosmos/feegrant/v1beta1/tx";
import { MsgExecuteContract } from "cosmjs-types/cosmwasm/wasm/v1/tx";
import axios from "axios";
import Config, { SSO_META } from "config";
import { store } from "reducer/store";
import {
  bulkSetUserInfo,
  clearUserInfo,
  setUserInfo,
} from "reducer/user.slice";
import { clearAuthInfos, setAuthInfos } from "reducer/wallet.slice";
import {
  AuthEntity,
  AuthInterface,
  Chain,
  ContractType,
  LoginType,
  MstrSharedRevenueTime,
  NFTType,
  Rarity,
  TRANSACTION_TYPE_ENUM,
  TicketRarity,
  Token,
  UserEntity,
  WALLET_PROVIDER,
} from "interfaces";
import ui from "./ui";
import utils from "./utils";
import {
  getContract,
  MAGContract,
  MSTRContract,
  StakeTokenContract,
  FTContract,
  NFTContractABI,
  USDTContract,
  USDTStakeContract,
} from "./contract";
import { clearSSOMeta, setSSOMeta } from "../reducer/ssometa.slice";
import { setLoginType } from "reducer/logintype.slice";
import keplrService from "./keplr";

//@ts-ignore
const Web3 = window.Web3;
//@ts-ignore
const ethereum = window.ethereum;
const web3 = new Web3(Web3.givenProvider);
const MaxUint =
  "115792089237316195423570985008687907853269984665640564039457584007913129639935";

function getChainId(type: LoginType) {
  switch (type) {
    case LoginType.BSC:
    case LoginType.Avalanche:
    case LoginType.Okex:
      return SSO_META().network[LoginType[type]].chainId;
    case LoginType.Terra:
      return "pisco-1";
  }
}

async function changeNetwork(type: LoginType) {
  let chainData = SSO_META().network[LoginType[type]];
  try {
    console.log(chainData?.chainId);
    // @ts-ignore
    await window.ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: chainData?.chainId }],
    });
  } catch (switchError: any) {
    console.log({ switchError });
    // This error code indicates that the chain has not been added to MetaMask.
    if (switchError.code === 4902) {
      console.log("Add net work");
      // @ts-ignore
      await window.ethereum
        .request({
          method: "wallet_addEthereumChain",
          params: [chainData],
        })
        .catch((error: any) => {
          console.log(error);
          throw error;
        });
    }
  }
}

async function checkNetwork(type: LoginType, src?: string) {
  console.log({ src, loginType: LoginType[type] });
  if (Config.AntiWalletAuth.includes(type)) return;

  switch (type) {
    case LoginType.BSC:
    case LoginType.Avalanche:
    case LoginType.Okex:
      // @ts-ignore
      const currentNetwork = await window.ethereum.request({
        method: "eth_chainId",
      });
      if (getChainId(type) !== currentNetwork) {
        await changeNetwork(type);
        // @ts-ignore
        const changedNetwork = await window.ethereum.request({
          method: "eth_chainId",
        });
        if (getChainId(type) !== changedNetwork) {
          throw new Error("failed to switch network");
        }
        store.dispatch(setLoginType(type));
      }
      break;
    case LoginType.Aura:
      await keplrService.keplrSuggestChain(type);
      await delay(500);
      store.dispatch(setLoginType(type));
      break;
  }
}

// config api method
async function post(url: string, data: any): Promise<any> {
  let rs = await fetch(`${utils.formatHostUrl(Config.ApiHost)}${url}`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
    body: JSON.stringify(data),
  });
  let refresh_token = rs.headers.get("refresh-token");
  if (refresh_token) {
    sessionStorage.setItem("token", refresh_token);
    console.log({ refresh_token });
  }
  switch (rs.status) {
    case 200:
      let tmp = await rs.json();
      return tmp;
    case 403:
      sessionStorage.clear();
      localStorage.clear();
      store.dispatch(clearUserInfo());
      store.dispatch(clearAuthInfos());
      ui.alertFailed("Your session is expired !", "Please sign in again !");
      throw new Error("forbidden");
    case 405:
      let tmps = await rs.json();
      throw new Error(tmps.message);
    case 423:
      try {
        let tmp = await rs.json();
        throw new Error(tmp.code);
      } catch (error) {
        throw error;
      }
    default:
      let err = await rs.json();
      throw err;
  }
}

async function get(url: string): Promise<any> {
  let rs = await fetch(`${utils.formatHostUrl(Config.ApiHost)}${url}`, {
    method: "GET",
    mode: "cors",
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
  });
  let refresh_token = rs.headers.get("refresh-token");
  if (refresh_token) {
    sessionStorage.setItem("token", refresh_token);
    console.log({ refresh_token });
  }
  switch (rs.status) {
    case 200:
      let tmp = await rs.json();
      return tmp;
    case 403:
      sessionStorage.clear();
      localStorage.clear();
      throw new Error("forbidden");
    case 405:
      let tmps = await rs.json();
      throw new Error(tmps.message);
    default:
      let err = await rs.json();
      throw err;
  }
}

async function getImage(url: string): Promise<any> {
  let rs = await fetch(`${utils.formatHostUrl(Config.ApiHost)}${url}`, {
    method: "GET",
    mode: "cors",
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
  });
  let refresh_token = rs.headers.get("refresh-token");
  if (refresh_token) {
    sessionStorage.setItem("token", refresh_token);
  }
  switch (rs.status) {
    case 200:
      let tmp = await rs.blob();
      return tmp;
    case 403:
    case 405:
      sessionStorage.clear();
      localStorage.clear();
      throw new Error("forbidden");
    default:
      let err = await rs.blob();
      throw err;
  }
}

async function postFormData(
  url: string,
  data: any,
  cb?: Function
): Promise<any> {
  var formdata = new FormData();
  for (var i in data) {
    formdata.append(i, data[i]);
  }
  let tmp: any = await axios.request({
    method: "post",
    url: `${utils.formatHostUrl(Config.ApiHost)}${url}`,
    data: formdata,
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
      Accept: "multipart/form-data",
    },
    onUploadProgress: (p) => {
      if (cb) cb(p);
    },
  });
  if (tmp.code === "forbidden") {
    window.location.href = "/auth";
    throw new Error("forbidden");
  }
  return tmp.data;
}

// terra
function getBalanceTerraMAG() {
  const eventId = utils.create_UUID();
  return new Promise((resolve, reject) => {
    document.dispatchEvent(
      new CustomEvent("getBalanceTerraMAG", {
        detail: {
          eventId,
        },
      })
    );

    const listener = (e: any) => {
      document.removeEventListener(`getBalanceTerraMAG.${eventId}`, listener);
      let parseValue = null;
      try {
        parseValue = web3.utils.fromWei(String(e.detail.balance), "mwei");
      } catch (err) {
        parseValue = null;
      }
      resolve("" + parseValue);
    };

    document.addEventListener(`getBalanceTerraMAG.${eventId}`, listener);
  });
}

function getBalanceTerraMSTR() {
  const eventId = utils.create_UUID();
  return new Promise((resolve, reject) => {
    document.dispatchEvent(
      new CustomEvent("getBalanceTerraMSTR", {
        detail: {
          eventId,
        },
      })
    );

    const listener = (e: any) => {
      document.removeEventListener(`getBalanceTerraMSTR.${eventId}`, listener);
      let parseValue = null;
      try {
        parseValue = web3.utils.fromWei(String(e.detail.balance), "mwei");
      } catch (err) {
        parseValue = null;
      }
      resolve("" + parseValue);
    };

    document.addEventListener(`getBalanceTerraMSTR.${eventId}`, listener);
  });
}

function depositTerraMSTR(amount: string, denom: any) {
  const eventId = utils.create_UUID();
  amount = web3.utils.toWei(amount, "mwei");
  return new Promise((resolve, reject) => {
    document.dispatchEvent(
      new CustomEvent("depositTerraMSTR", {
        detail: {
          amount,
          eventId,
          denom,
        },
      })
    );

    const listener = (e: any) => {
      document.removeEventListener(`depositedTerraMSTR.${eventId}`, listener);
      if (!e.detail || e.detail?.error) {
        reject(e.detail?.error ?? "common_failed");
        return;
      }
      resolve(e.detail);
    };
    document.addEventListener(`depositedTerraMSTR.${eventId}`, listener);
  });
}

function depositTerraMAG(amount: string, denom: any) {
  const eventId = utils.create_UUID();
  amount = web3.utils.toWei(amount, "mwei");
  return new Promise((resolve, reject) => {
    document.dispatchEvent(
      new CustomEvent("depositTerraMAG", {
        detail: {
          amount,
          eventId,
          denom,
        },
      })
    );

    const listener = (e: any) => {
      document.removeEventListener(`depositedTerraMAG.${eventId}`, listener);
      if (!e.detail || e.detail?.error) {
        reject(e.detail?.error ?? "common_failed");
        return;
      }
      resolve(e.detail);
    };
    document.addEventListener(`depositedTerraMAG.${eventId}`, listener);
  });
}

// bsc
async function getMSTRBalance(login_type?: LoginType) {
  try {
    let value = login_type ? login_type : store.getState().logintype;
    let balance = 0;
    await checkNetwork(value, "getMSTR");
    const authAddress = store.getState().wallets[value][AuthInterface.identity];
    switch (value) {
      case LoginType.BSC:
      case LoginType.Avalanche:
      case LoginType.Okex:
        // @ts-ignore
        MSTRContract.options.address = getContract(
          ContractType.MSTR,
          // @ts-ignore
          Chain[LoginType[value]]
        );
        balance = await MSTRContract.methods.balanceOf(authAddress).call();
        balance = web3.utils.fromWei(balance);
        break;
      case LoginType.Aura:
        balance = await keplrService.getTokenBalance(
          authAddress,
          getContract(
            ContractType.MSTR,
            // @ts-ignore
            Chain[LoginType[value]]
          )
        );
        balance = web3.utils.fromWei(balance);
        break;
      case LoginType.Terra:
        let data = await getBalanceTerraMSTR();
        balance = data === null ? null : Number(data);
        break;
      default:
        break;
    }
    store.dispatch(setUserInfo({ mstr_wallet: balance }));
    return balance;
  } catch (error) {
    console.log(error);
  }
}

async function getMAGBalance(login_type?: LoginType) {
  try {
    let value = login_type ? login_type : store.getState().logintype;
    let balance = 0;
    const authAddress = store.getState().wallets[value][AuthInterface.identity];

    switch (value) {
      case LoginType.BSC:
      case LoginType.Avalanche:
      case LoginType.Okex:
        await checkNetwork(value, "getMAG");
        // const authAddress =
        //   store.getState().wallets[LoginType.BSC][AuthInterface.identity];
        balance = await MAGContract.methods.balanceOf(authAddress).call();
        balance = web3.utils.fromWei(balance);
        break;
        case LoginType.Aura:
        balance = await keplrService.getTokenBalance(
          authAddress,
          getContract(
            ContractType.MAG,
            // @ts-ignore
            Chain[LoginType[value]]
          )
        );
        balance = web3.utils.fromWei(balance);
        break;
      case LoginType.Terra:
        let data = await getBalanceTerraMAG();
        balance = data === null ? null : Number(data);
        break;
      default:
        break;
    }
    console.log('mag balance ', balance)
    store.dispatch(setUserInfo({ mag_wallet: balance }));
    return balance;
  } catch (error) {
    console.log(error);
  }
}

async function getUSDTBalance(login_type?: LoginType) {
  try {
    let value = login_type ? login_type : store.getState().logintype;
    let balance = 0;
    switch (value) {
      case LoginType.BSC:
      case LoginType.Avalanche:
      case LoginType.Okex:
        await checkNetwork(value, "getUSDT");
        const authAddress =
          store.getState().wallets[LoginType.BSC][AuthInterface.identity];
        balance = await USDTContract.methods.balanceOf(authAddress).call();
        balance = web3.utils.fromWei(balance);
        break;
      default:
        break;
    }
    store.dispatch(setUserInfo({ usdt_wallet: balance }));
    return balance;
  } catch (error) {
    console.log(error);
  }
}

async function getTicketsBalance(loginType: LoginType) {
  try {
    let balance = 0;
    const authAddress =
      store.getState().wallets[loginType][AuthInterface.identity];
    let obj: any = {};
    await Promise.all(
      [
        ContractType.TicketCO,
        ContractType.TicketUC,
        ContractType.TicketRA,
        ContractType.TicketEP,
        ContractType.TicketGE,
        ContractType.TicketGL,
      ].map(async (e) => {
        switch (loginType) {
          case LoginType.BSC:
          case LoginType.Avalanche:
          case LoginType.Okex:
            // @ts-ignore
            FTContract.options.address = getContract(
              e,
              // @ts-ignore
              Chain[LoginType[loginType]]
            );
            balance = await FTContract.methods.balanceOf(authAddress).call();
            obj[`${e}_wallet`] = balance;
            break;
          case LoginType.Aura:
            balance = await keplrService.getTokenBalance(
              authAddress,
              getContract(
                e,
                // @ts-ignore
                Chain[LoginType[loginType]]
              )
            );
            console.log({ balance });
            obj[`${e}_wallet`] = balance;
            break;
        }
      })
    );
    return obj;
  } catch (error) {
    // console.log(error);
    return {};
  }
}

async function getAllFTBalance(loginType: LoginType) {
  try {
    let balance = 0;
    const authAddress =
      store.getState().wallets[loginType][AuthInterface.identity];
    let obj: any = {};
    await Promise.all(
      [ContractType.Elixir, ContractType.MutatedGen].map(async (e) => {
        switch (loginType) {
          case LoginType.BSC:
          case LoginType.Avalanche:
          case LoginType.Okex:
            // @ts-ignore
            FTContract.options.address = getContract(
              e,
              // @ts-ignore
              Chain[LoginType[loginType]]
            );
            balance = await FTContract.methods.balanceOf(authAddress).call();
            break;
          case LoginType.Aura:
            balance = await keplrService.getTokenBalance(
              authAddress,
              getContract(
                e,
                // @ts-ignore
                Chain[LoginType[loginType]]
              )
            );
            break;
        }
        switch (e) {
          case ContractType.Elixir:
            obj[`elixir_wallet`] = balance;
            break;
          case ContractType.MutatedGen:
            obj[`mutategen_wallet`] = balance;
            break;
        }
      })
    );
    return obj;
  } catch (error) {
    // console.log(error);
    return {};
  }
}

async function getTokenBalance(login_type?: LoginType) {
  try {
    if (!sessionStorage.getItem("token")) {
      console.log("not have token");
      return;
    }
    let value = login_type ? login_type : store.getState().logintype;
    console.log({ value });
    let magBalance = 0;
    let mstrBalance = 0;
    let usdtBalance = 0;
    let ticketsBalance = {};
    let ftsBalance = {};
    await checkNetwork(value, "getToken");
    const authAddress = store.getState().wallets[value][AuthInterface.identity];

    switch (value) {
      case LoginType.BSC:
      case LoginType.Avalanche:
      case LoginType.Okex:
        // @ts-ignore
        MAGContract.options.address = getContract(
          ContractType.MAG,
          // @ts-ignore
          Chain[LoginType[value]]
        );
        magBalance = await MAGContract.methods.balanceOf(authAddress).call();
        magBalance = web3.utils.fromWei(magBalance);
        // @ts-ignore
        MSTRContract.options.address = getContract(
          ContractType.MSTR,
          // @ts-ignore
          Chain[LoginType[value]]
        );
        mstrBalance = await MSTRContract.methods.balanceOf(authAddress).call();
        mstrBalance = web3.utils.fromWei(mstrBalance);
        try {
          // @ts-ignore
          USDTContract.options.address = getContract(
            ContractType.USDT,
            // @ts-ignore
            Chain[LoginType[value]]
          );
          usdtBalance = await USDTContract.methods
            .balanceOf(authAddress)
            .call();
          usdtBalance = web3.utils.fromWei(usdtBalance);
        } catch (err) {}
        ticketsBalance = await getTicketsBalance(value);
        ftsBalance = await getAllFTBalance(value);
        break;
      case LoginType.Aura:
        magBalance = await keplrService.getTokenBalance(
          authAddress,
          getContract(
            ContractType.MAG,
            // @ts-ignore
            Chain[LoginType[value]]
          )
        );
        magBalance = web3.utils.fromWei(magBalance);

        mstrBalance = await keplrService.getTokenBalance(
          authAddress,
          getContract(
            ContractType.MSTR,
            // @ts-ignore
            Chain[LoginType[value]]
          )
        );
        mstrBalance = web3.utils.fromWei(mstrBalance);

        try {
          usdtBalance = await keplrService.getTokenBalance(
            authAddress,
            getContract(
              ContractType.USDT,
              // @ts-ignore
              Chain[LoginType[value]]
            )
          );
          usdtBalance = web3.utils.fromWei(usdtBalance, "mwei");
        } catch (err) {}
        ticketsBalance = await getTicketsBalance(value);
        ftsBalance = await getAllFTBalance(value);
        break;
      case LoginType.Terra:
        await new Promise((resolve) => setTimeout(resolve, 200));
        let magData = await getBalanceTerraMAG();
        let mstrData = await getBalanceTerraMSTR();

        magBalance = magData === null ? null : Number(magData);
        mstrBalance = mstrData === null ? null : Number(mstrData);
        break;
      default:
        magBalance = 0;
        mstrBalance = 0;
        break;
    }
    store.dispatch(
      setUserInfo({
        mag_wallet: magBalance,
        mstr_wallet: mstrBalance,
        usdt_wallet: usdtBalance,
        ...ticketsBalance,
        ...ftsBalance,
      })
    );
  } catch (error) {
    console.log(error);
  }
}

async function getMainBalanceChain(address: string, loginType: LoginType) {
  let value = store.getState().logintype;
  if (value !== loginType) {
    return null;
  }
  await checkNetwork(loginType, "getMainBalance");
  let balance;

  switch (loginType) {
    case LoginType.BSC:
      balance = await web3.eth.getBalance(address);
      balance = web3.utils.fromWei(balance);
      store.dispatch(setUserInfo({ bnb_wallet: balance }));
      break;
    case LoginType.Avalanche:
      balance = await web3.eth.getBalance(address);
      balance = web3.utils.fromWei(balance);
      store.dispatch(setUserInfo({ avax_wallet: balance }));
      break;
    case LoginType.Okex:
      balance = await web3.eth.getBalance(address);
      balance = web3.utils.fromWei(balance);
      store.dispatch(setUserInfo({ okt_wallet: balance }));
      break;
    case LoginType.Aura:
      balance = await keplrService.getBalance(address);
      balance = web3.utils.fromWei(balance, "mwei");
      store.dispatch(setUserInfo({ aura_wallet: balance }));
  }
  return balance;
}

function getRandomRpc(chain: Chain) {
  let network = SSO_META().network;
  let chainData = network[getKeyByValue(Chain, chain)];
  let rpcUrls = chainData?.rpcUrls ?? [];
  let randomRpc = rpcUrls[Math.floor(Math.random() * rpcUrls.length)];
  if ([Chain.BSC, Chain.Avalanche, Chain.Okex].includes(chain)) {
    let newWeb3 = new Web3(new Web3.providers.HttpProvider(randomRpc));
    return newWeb3;
  }
  return web3;
}

function getKeyByValue(object: any, value: any) {
  return Object.keys(object).find((key) => object[key] === value);
}

async function GetNftBalance(
  contractType: ContractType,
  chain = Chain.BSC,
  provider = web3
) {
  let address = getContract(contractType, chain);
  const contract = new provider.eth.Contract(NFTContractABI, address);
  const authAddress =
    store.getState().wallets[LoginType.BSC][AuthInterface.identity];
  const data = await contract.methods.getOwnedToken(authAddress).call();

  return data.map((nft: any) => {
    const tokenId = nft.tokenId;
    const metadata = nft.nftData;
    return {
      tokenId: tokenId,
      type: metadata.nftType,
      chain_id: chain,
    };
  });
}

async function GetNfts() {
  let value = store.getState().logintype;
  //@ts-ignore
  let chain = Chain[LoginType[value]];

  let nfts: any = [];
  const getNfts = async (callback: any) => {
    const result = await callback;
    nfts = [...nfts, ...result];
  };

  try {
    let newProvider = getRandomRpc(chain);
    await Promise.allSettled([
      getNfts(GetNftBalance(ContractType.Mongen, chain, newProvider)),
      getNfts(GetNftBalance(ContractType.Plot, chain, newProvider)),
    ]);
  } catch (err) {
    console.log(err);
  }
  let plotIds = nfts
    .filter((nft: any) => Number(nft.type) === NFTType.Plot)
    .map((nft: any) => Number(nft.tokenId));

  let mongenIds = nfts
    .filter((nft: any) => Number(nft.type) === NFTType.Mongen)
    .map((nft: any) => Number(nft.tokenId));

  let res = await getItemsOnchain(plotIds, mongenIds);
  return res;
}

async function depositMSTR(amount: string, login_type?: LoginType) {
  try {
    let value = login_type ? login_type : store.getState().logintype;
    await checkNetwork(value, "deposit");
    switch (value) {
      case LoginType.BSC:
      case LoginType.Avalanche:
      case LoginType.Okex:
        const account = await ethereum.request({
          method: "eth_requestAccounts",
        });
        let connectedWallet;
        if (store.getState().wallets[value]) {
          connectedWallet =
            store.getState().wallets[value][AuthInterface.identity];
        }
        if (
          connectedWallet?.toString().toLowerCase() !==
          account[0]?.toString().toLowerCase()
        ) {
          throw new Error("metamask_wallet_not_match");
        }

        // new method
        let stakeContract = getContract(
          ContractType.STAKING,
          // @ts-ignore
          Chain[LoginType[value]]
        );

        let MSTRapprove = await MSTRContract.methods
          .allowance(connectedWallet, stakeContract)
          .call();

        let remainAllowence = web3.utils.fromWei(MSTRapprove);

        if (Number(remainAllowence ?? 0) < Number(amount)) {
          await MSTRContract.methods
            .approve(stakeContract, MaxUint)
            .send({ from: connectedWallet });
        }

        StakeTokenContract.options.address = getContract(
          ContractType.STAKING,
          // @ts-ignore
          Chain[LoginType[value]]
        );

        await StakeTokenContract.methods
          // @ts-ignore
          .stakeMSTR(
            web3.utils.toWei(amount, "ether"),
            MstrSharedRevenueTime.Infinity
          )
          .send({ from: connectedWallet });
        // end
        break;

      case LoginType.Aura:
        const keplrAddress = await keplrService.connect(WALLET_PROVIDER.KEPLR);

        const auraAuthAddress =
          store.getState().wallets[value][AuthInterface.identity];

        if (
          auraAuthAddress?.toString().toLowerCase() !==
          keplrAddress?.toString().toLowerCase()
        ) {
          throw new Error("metamask_wallet_not_match");
        }

        // new method
        let auraStakeContract = getContract(
          ContractType.STAKING,
          // @ts-ignore
          Chain[LoginType[value]]
        );
        let auraMstrContract = getContract(
          ContractType.MSTR,
          // @ts-ignore
          Chain[LoginType[value]]
        );

        let AuaMSTRapprove = await keplrService.getTokenAllowance(
          auraMstrContract,
          auraAuthAddress,
          auraStakeContract
        );

        let auraRemainAllowence = web3.utils.fromWei(AuaMSTRapprove);

        if (Number(auraRemainAllowence ?? 0) < Number(amount)) {
          await keplrService.execute(auraAuthAddress, auraMstrContract, {
            increase_allowance: {
              amount: web3.utils.toWei("100000000", "ether"),
              spender: auraStakeContract,
            },
          });
        }

        await keplrService.execute(auraAuthAddress, auraStakeContract, {
          stake: {
            amount: `${web3.utils.toWei(`${amount}`, "ether")}`,
            duration: MstrSharedRevenueTime.Infinity,
            token: auraMstrContract,
          },
        });
        break;
      case LoginType.Terra:
        try {
          const denom = localStorage.getItem("terra_denom_fee");
          await depositTerraMSTR(amount, denom ?? "uusd");
        } catch (err) {
          throw new Error(err.toString());
        }
        break;
      default:
        break;
    }
  } catch (error) {
    throw error;
  }
}

async function depositPeriodMSTR(
  amount: string,
  duration: MstrSharedRevenueTime,
  login_type?: LoginType
) {
  try {
    let value = login_type ? login_type : store.getState().logintype;
    switch (value) {
      case LoginType.BSC:
      case LoginType.Avalanche:
      case LoginType.Okex:
        await checkNetwork(value, "deposit");
        const account = await ethereum.request({
          method: "eth_requestAccounts",
        });
        let connectedWallet;
        if (store.getState().wallets[value]) {
          connectedWallet =
            store.getState().wallets[value][AuthInterface.identity];
        }
        if (
          connectedWallet?.toString().toLowerCase() !==
          account[0]?.toString().toLowerCase()
        ) {
          throw new Error("metamask_wallet_not_match");
        }

        let stakeContract = getContract(
          ContractType.STAKING,
          // @ts-ignore
          Chain[LoginType[value]]
        );

        let MSTRapprove = await MSTRContract.methods
          .allowance(connectedWallet, stakeContract)
          .call();

        let remainAllowence = web3.utils.fromWei(MSTRapprove);

        if (Number(remainAllowence) < Number(amount)) {
          await MSTRContract.methods
            .approve(stakeContract, MaxUint)
            .send({ from: connectedWallet });
        }

        StakeTokenContract.options.address = getContract(
          ContractType.STAKING,
          // @ts-ignore
          Chain[LoginType[value]]
        );

        await StakeTokenContract.methods
          // @ts-ignore
          .stakeMSTR(web3.utils.toWei(amount, "ether"), duration)
          .send({ from: connectedWallet });
        break;
      case LoginType.Terra:
        try {
          const denom = localStorage.getItem("terra_denom_fee");
          await depositTerraMSTR(amount, denom ?? "uusd");
        } catch (err) {
          throw new Error(err.toString());
        }
        break;
      case LoginType.Aura:
        const keplrAddress = await keplrService.connect(WALLET_PROVIDER.KEPLR);

        const auraAuthAddress =
          store.getState().wallets[value][AuthInterface.identity];

        if (
          auraAuthAddress?.toString().toLowerCase() !==
          keplrAddress?.toString().toLowerCase()
        ) {
          throw new Error("metamask_wallet_not_match");
        }

        // new method
        let auraStakeContract = getContract(
          ContractType.STAKING,
          // @ts-ignore
          Chain[LoginType[value]]
        );
        let auraMstrContract = getContract(
          ContractType.MSTR,
          // @ts-ignore
          Chain[LoginType[value]]
        );

        let AuaMSTRapprove = await keplrService.getTokenAllowance(
          auraMstrContract,
          auraAuthAddress,
          auraStakeContract
        );

        let auraRemainAllowence = web3.utils.fromWei(AuaMSTRapprove);

        if (Number(auraRemainAllowence ?? 0) < Number(amount)) {
          await keplrService.execute(auraAuthAddress, auraMstrContract, {
            increase_allowance: {
              amount: web3.utils.toWei("100000000", "ether"),
              spender: auraStakeContract,
            },
          });
        }

        await keplrService.execute(auraAuthAddress, auraStakeContract, {
          stake: {
            amount: `${web3.utils.toWei(`${amount}`, "ether")}`,
            duration: duration,
            token: auraMstrContract,
          },
        });
        break;
      default:
        break;
    }
  } catch (error) {
    throw error;
  }
}

async function depositMAG(amount: string, login_type?: LoginType) {
  try {
    let value = login_type ? login_type : store.getState().logintype;
    switch (value) {
      case LoginType.BSC:
      case LoginType.Avalanche:
      case LoginType.Okex:
        await checkNetwork(value, "deposit");
        const account = await ethereum.request({
          method: "eth_requestAccounts",
        });
        let connectedWallet;
        if (store.getState().wallets[value]) {
          connectedWallet =
            store.getState().wallets[value][AuthInterface.identity];
        }
        if (
          connectedWallet?.toString().toLowerCase() !==
          account[0]?.toString().toLowerCase()
        ) {
          throw new Error("metamask_wallet_not_match");
        }
        // @ts-ignore
        MAGContract.options.address = getContract(
          ContractType.MAG,
          // @ts-ignore
          Chain[LoginType[value]]
        );
        await MAGContract.methods
          // @ts-ignore
          .transfer(
            // @ts-ignore
            getContract(ContractType.STAKING, Chain[LoginType[value]]),
            web3.utils.toWei(amount, "ether")
          )
          .send({ from: account[0] });
        break;
      case LoginType.Aura:
        const keplrAddress = await keplrService.connect(WALLET_PROVIDER.KEPLR);

        const auraAuthAddress =
          store.getState().wallets[value][AuthInterface.identity];

        if (
          auraAuthAddress?.toString().toLowerCase() !==
          keplrAddress?.toString().toLowerCase()
        ) {
          throw new Error("metamask_wallet_not_match");
        }

        // new method
        let auraStakeContract = getContract(
          ContractType.STAKING,
          // @ts-ignore
          Chain[LoginType[value]]
        );
        let auraMagContract = getContract(
          ContractType.MAG,
          // @ts-ignore
          Chain[LoginType[value]]
        );

        let AuraMAGapprove = await keplrService.getTokenAllowance(
          auraMagContract,
          auraAuthAddress,
          auraStakeContract
        );

        let auraRemainAllowence = web3.utils.fromWei(AuraMAGapprove);

        if (Number(auraRemainAllowence ?? 0) < Number(amount)) {
          await keplrService.execute(auraAuthAddress, auraMagContract, {
            increase_allowance: {
              amount: web3.utils.toWei("100000000", "ether"),
              spender: auraStakeContract,
            },
          });
        }

        await keplrService.execute(auraAuthAddress, auraStakeContract, {
          stake: {
            amount: `${web3.utils.toWei(`${amount}`, "ether")}`,
            duration: MstrSharedRevenueTime.Infinity,
            token: auraMagContract,
          },
        });
        break;
      case LoginType.Terra:
        const denom = localStorage.getItem("terra_denom_fee");
        await depositTerraMAG(amount, denom ?? "uusd");
        break;
      default:
        break;
    }
  } catch (error) {
    console.log({ error });
    throw error;
  }
}

async function depositUSDT(amount: string, login_type?: LoginType) {
  try {
    let value = login_type ? login_type : store.getState().logintype;
    value = LoginType.BSC;
    switch (value) {
      case LoginType.BSC:
      case LoginType.Avalanche:
      case LoginType.Okex:
        await checkNetwork(value, "deposit");
        const account = await ethereum.request({
          method: "eth_requestAccounts",
        });

        let connectedWallet;
        if (store.getState().wallets[value]) {
          connectedWallet =
            store.getState().wallets[value][AuthInterface.identity];
        }
        if (
          connectedWallet?.toString().toLowerCase() !==
          account[0]?.toString().toLowerCase()
        ) {
          throw new Error("metamask_wallet_not_match");
        }

        // new method
        let stakeContract = getContract(
          ContractType.USDTStaking,
          // @ts-ignore
          Chain[LoginType[value]]
        );

        USDTContract.options.address = getContract(
          ContractType.USDT,
          // @ts-ignore
          Chain[LoginType[value]]
        );

        let USDTapprove = await USDTContract.methods
          .allowance(connectedWallet, stakeContract)
          .call();

        let remainAllowence = web3.utils.fromWei(USDTapprove);
        console.log({ remainAllowence });

        if (Number(remainAllowence ?? 0) < Number(amount)) {
          await USDTContract.methods
            .approve(stakeContract, MaxUint)
            .send({ from: connectedWallet });
        }

        USDTStakeContract.options.address = getContract(
          ContractType.USDTStaking,
          // @ts-ignore
          Chain[LoginType[value]]
        );

        await USDTStakeContract.methods
          // @ts-ignore
          .stakeUSDT(web3.utils.toWei(amount, "ether"))
          .send({ from: connectedWallet });
        // end
        break;
      default:
        break;
    }
  } catch (error) {
    throw error;
  }
}

async function checkTrueWallet(login_type: LoginType, wallet_address: string) {
  console.log({ login_type, wallet_address });
  switch (login_type) {
    case LoginType.BSC:
    case LoginType.Avalanche:
    case LoginType.Okex:
      await checkNetwork(login_type, "true wallet");
      const account = await ethereum.request({ method: "eth_requestAccounts" });
      if (
        wallet_address?.toString().toLowerCase() !==
        account[0]?.toString().toLowerCase()
      ) {
        throw new Error("metamask_wallet_not_match");
      }
      break;
    default:
      break;
  }
}

async function requestWithdraw(
  token: Token,
  amount: string,
  nonce: string,
  timestamp: any,
  signature: string,
  chain: Chain
) {
  try {
    let account, connectedWallet, auraAuthAddress;
    // @ts-ignore
    let loginType = LoginType[Chain[chain]];
    await checkNetwork(loginType, "withdraw");

    if (Config.EtherChain.includes(chain)) {
      account = await ethereum.request({ method: "eth_requestAccounts" });
      if (store.getState().wallets[loginType]) {
        connectedWallet =
          store.getState().wallets[loginType][AuthInterface.identity];
        if (
          connectedWallet?.toString().toLowerCase() !==
          account[0]?.toString().toLowerCase()
        ) {
          throw new Error("metamask_wallet_not_match");
        }
      }
    } else {
      const keplrAddress = await keplrService.connect(WALLET_PROVIDER.KEPLR);
      auraAuthAddress =
        store.getState().wallets[loginType][AuthInterface.identity];

      if (
        auraAuthAddress?.toString().toLowerCase() !==
        keplrAddress?.toString().toLowerCase()
      ) {
        throw new Error("metamask_wallet_not_match");
      }
    }

    let tx;
    let gasLimit = SSO_META()?.network[Chain[chain]]?.gasLimit ?? 50000000;

    switch (token) {
      case Token.MAG:
        if (Config.EtherChain.includes(chain)) {
          //@ts-ignore
          StakeTokenContract.options.address = getContract(
            ContractType.STAKING,
            chain
          );
          StakeTokenContract.options.gasLimit = gasLimit;
          tx = await StakeTokenContract.methods
            .unstakeMAG(amount, nonce, timestamp, signature)
            .send({
              from: account[0],
            });
        } else {
          await keplrService.execute(
            auraAuthAddress,
            getContract(ContractType.STAKING, chain),
            {
              unstake: {
                signature,
                msg: {
                  token: getContract(ContractType.MAG, chain),
                  amount,
                  nonce,
                  timestamp: timestamp.toString(),
                },
              },
            }
          );
        }
        break;
      case Token.MSTR:
        if (Config.EtherChain.includes(chain)) {
          //@ts-ignore
          StakeTokenContract.options.address = getContract(
            ContractType.STAKING,
            chain
          );
          StakeTokenContract.options.gasLimit = gasLimit;
          tx = await StakeTokenContract.methods
            .unstakeMSTR(amount, nonce, timestamp, signature)
            .send({
              from: account[0],
            });
        } else {
          console.log({
            unstake: {
              contract: getContract(ContractType.STAKING, chain),
              signature,
              msg: {
                token: getContract(ContractType.MSTR, chain),
                amount,
                nonce,
                timestamp: timestamp.toString(),
              },
            },
          });

          await keplrService.execute(
            auraAuthAddress,
            getContract(ContractType.STAKING, chain),
            {
              unstake: {
                signature,
                msg: {
                  token: getContract(ContractType.MSTR, chain),
                  amount,
                  nonce,
                  timestamp: timestamp.toString(),
                },
              },
            }
          );
        }
        break;
      case Token.USDT:
        if (Config.EtherChain.includes(chain)) {
          USDTStakeContract.options.address = getContract(
            ContractType.USDTStaking,
            chain
          );
          USDTStakeContract.options.gasLimit = gasLimit;
          tx = await USDTStakeContract.methods
            .unstakeUSDT(amount, nonce, timestamp, signature)
            .send({
              from: account[0],
            });
        }
        break;
      default:
        throw new Error("invalid_token");
    }
  } catch (error) {
    throw error;
  }
}

async function depositTicket(
  amount: string,
  login_type?: LoginType,
  ticket_type?: ContractType
) {
  try {
    let value = login_type ? login_type : store.getState().logintype;
    await checkNetwork(value, "deposit ticket");
    // @ts-ignore
    let address = getContract(
      // @ts-ignore
      ContractType[ticket_type],
      // @ts-ignore
      Chain[LoginType[value]]
    );
    switch (value) {
      case LoginType.Aura:
        const keplrAddress = await keplrService.connect(WALLET_PROVIDER.KEPLR);

        const auraAuthAddress =
          store.getState().wallets[value][AuthInterface.identity];

        if (
          auraAuthAddress?.toString().toLowerCase() !==
          keplrAddress?.toString().toLowerCase()
        ) {
          throw new Error("metamask_wallet_not_match");
        }

        await keplrService.execute(auraAuthAddress, address, {
          burn: {
            amount: `${amount}`,
          },
        });
        break;
      case LoginType.BSC:
      case LoginType.Avalanche:
      case LoginType.Okex:
        const account = await ethereum.request({
          method: "eth_requestAccounts",
        });
        let connectedWallet;
        if (store.getState().wallets[value]) {
          connectedWallet =
            store.getState().wallets[value][AuthInterface.identity];
        }
        if (
          connectedWallet?.toString().toLowerCase() !==
          account[0]?.toString().toLowerCase()
        ) {
          throw new Error("metamask_wallet_not_match");
        }

        if (!address) {
          throw new Error("contract_not_found");
        }
        FTContract.options.address = address;
        await FTContract.methods.burn(amount).send({ from: account[0] });
        break;
      default:
        throw new Error("only_in_bsc_chain");
    }
  } catch (error) {
    throw error;
  }
}

// authenticate handle
async function getNonce() {
  let rs = await api.post("/nonce/create-nonce", {});
  return rs.nonce;
}

async function getMetamaskSign() {
  let nonce = await getNonce();
  const account = await ethereum.request({ method: "eth_requestAccounts" });
  let sign = await web3.eth.personal.sign(nonce, account[0], "publicpassword");
  return { nonce, sign };
}

async function getKeplrSign() {
  let nonce = await getNonce();
  let accountAddress = await keplrService.connect(WALLET_PROVIDER.KEPLR);
  let signature = await keplrService.signMessage(accountAddress, nonce);
  return { nonce, sign: JSON.stringify(signature) };
}

async function registerMetaMask(referral?: string, type?: LoginType) {
  await api.checkNetwork(type);
  let nonce = await getNonce();
  await ethereum._metamask.isUnlocked();
  const account = await ethereum.request({ method: "eth_requestAccounts" });
  let sign = await web3.eth.personal.sign(nonce, account[0], "publicpassword");
  ui.showCaptcha(async (captcha_id: string, captcha_nonce: number) => {
    let { customerInfo, token, authInfos } = await api.post("/dao/register", {
      sign,
      nonce,
      type,
      referral,
      captcha_id,
      captcha_nonce,
    });
    await setStateLogin(token, customerInfo, authInfos, type);
  });
}

async function registerCosmos(
  referral?: string,
  type: LoginType = LoginType.Aura
) {
  try {
    let nonce = await getNonce();
    let accountAddress = await keplrService.connect(WALLET_PROVIDER.KEPLR);
    await checkNetwork(type);

    let signature = await keplrService.signMessage(accountAddress, nonce);
    console.log({ signature });

    ui.showCaptcha(async (captcha_id: string, captcha_nonce: number) => {
      let { customerInfo, token, authInfos } = await api.post("/dao/register", {
        sign: JSON.stringify(signature),
        nonce,
        type,
        referral,
        captcha_id,
        captcha_nonce,
      });
      await setStateLogin(token, customerInfo, authInfos, type);
    });
  } catch (err) {
    console.log({ err });
  }
}

async function login(type: LoginType) {
  sessionStorage.clear();
  let nonce = await getNonce();
  await api.checkNetwork(type);
  const account = await ethereum.request({ method: "eth_requestAccounts" });
  console.log({ account });
  let sign = await web3.eth.personal.sign(nonce, account[0], "publicpassword");
  let { customerInfo, token, authInfos, is_2fa_enabled } = await api.post(
    "/dao/login-dao",
    {
      sign,
      nonce,
      type,
    }
  );
  if (is_2fa_enabled) {
    nonce = await getNonce();
    sign = await web3.eth.personal.sign(nonce, account[0], "publicpassword");
    ui.confirmAction("Two factor authentication", "", "code", (otp: string) => {
      api
        .loginWith2FA({ otp, sign, nonce, type })
        .then(() => {
          console.log("login done");
        })
        .catch((err: any) => {
          ui.alertFailed("Login failed !", `${err.message}`);
        });
    });
  } else {
    await api.setStateLogin(token, customerInfo, authInfos, type);
  }
}

async function loginByPassword(email: string, password: string) {
  let {
    customerInfo,
    token,
    authInfos,
    is_2fa_enabled,
  }: {
    customerInfo: UserEntity;
    token: string;
    authInfos: AuthEntity[];
    is_2fa_enabled: boolean;
  } = await api.post("/dao/login-dao", {
    email,
    password,
    type: LoginType.Email,
  });
  if (is_2fa_enabled) {
    ui.confirmAction("Two factor authentication", "", "code", (otp: string) => {
      api
        .loginWith2FA({ otp, email, password, type: LoginType.Email })
        .then(() => {
          console.log("login done");
        })
        .catch((err) => {
          ui.alertFailed("Login failed !", `${err.message}`);
        });
    });
  } else {
    await api.setStateLogin(token, customerInfo, authInfos, LoginType.Email);
  }
}

async function loginWith2FA({
  otp,
  email,
  password,
  type,
  sign,
  nonce,
  social_access_token,
}: any) {
  let { token, authInfo, customerInfo, authInfos } = await api.post(
    "/dao/2fa-login",
    {
      otp,
      email,
      sign,
      nonce,
      password,
      type,
      social_access_token,
    }
  );
  await setStateLogin(
    token,
    customerInfo,
    authInfos,
    authInfo[AuthInterface.type]
  );
}

async function setStateLogin(
  token: string,
  customerInfo: any,
  authInfos: any,
  loginType: LoginType,
  isWalletConnect: boolean = false
) {
  sessionStorage.setItem("token", token);
  let newLoginType = loginType;
  if (Config.AntiWalletAuth.includes(loginType)) {
    const existedWallet = authInfos.filter(
      (authInfo: any) =>
        !Config.AntiWalletAuth.includes(authInfo[AuthInterface.type])
    );
    if (existedWallet.length > 0) {
      let currentType = existedWallet[0][AuthInterface.type];
      newLoginType = currentType ?? LoginType.BSC ?? loginType;
    }
  }
  store.dispatch(setLoginType(newLoginType));
  if (isWalletConnect) {
    sessionStorage.setItem("isWalletConnect", "true");
  }
  store.dispatch(bulkSetUserInfo(customerInfo));
  store.dispatch(setAuthInfos(authInfos));
  await getTokenBalance(newLoginType);
}

async function registerTerra(
  sign: any,
  nonce: any,
  type: LoginType,
  referral: string
) {
  let data;
  ui.showCaptcha(async (captcha_id: string, captcha_nonce: number) => {
    data = await post("/dao/register", {
      sign,
      nonce,
      type,
      referral,
      captcha_id,
      captcha_nonce,
    });
  });
  return data;
}

async function loginTerra(sign: any, nonce: any, type: LoginType) {
  return post("/dao/login-dao", { sign, nonce, type });
}

async function loginCosmos(type: LoginType) {
  let nonce = await getNonce();
  let accountAddress = await keplrService.connect(WALLET_PROVIDER.KEPLR);
  await checkNetwork(type);
  let signature = await keplrService.signMessage(accountAddress, nonce);

  let { customerInfo, token, authInfos, is_2fa_enabled } = await api.post(
    "/dao/login-dao",
    {
      sign: JSON.stringify(signature),
      nonce,
      type,
    }
  );
  await api.setStateLogin(token, customerInfo, authInfos, type);
}

async function registerBySocial(
  social_access_token: string,
  type: LoginType,
  referral?: string,
  access_token_secret?: string
) {
  let data;
  ui.showCaptcha(async (captcha_id: string, captcha_nonce: number) => {
    data = await post("/dao/register", {
      social_access_token,
      type,
      referral,
      captcha_id,
      captcha_nonce,
      access_token_secret,
    });
    let { customerInfo, token, authInfos } = data;
    await setStateLogin(token, customerInfo, authInfos, type);
  });
  return data;
}

async function loginBySocial(
  social_access_token: string,
  type: LoginType,
  access_token_secret?: string
) {
  let { customerInfo, token, authInfos, is_2fa_enabled } = await post(
    "/dao/login-dao",
    {
      social_access_token,
      type,
      access_token_secret,
    }
  );
  if (is_2fa_enabled) {
    ui.confirmAction("Two factor authentication", "", "code", (otp: string) => {
      api
        .loginWith2FA({ otp, type, social_access_token })
        .then(() => {
          console.log("login done");
        })
        .catch((err: any) => {
          ui.alertFailed("Login failed !", `${err.message}`);
        });
    });
  } else {
    await api.setStateLogin(token, customerInfo, authInfos, type);
  }
}

// dao api method
async function uploadImage(image: any): Promise<any> {
  return postFormData(
    `${utils.formatHostUrl(Config.ApiHost)}/file/upload-image`,
    { image }
  );
}

async function getMessages(message: string) {
  return post("/message/get-dao-messages", { message });
}

async function updateProfile(name: string) {
  return post("/dao/update-customer-profile", { name });
}

async function requestChangeMail(email: string) {
  return post("/dao/request-change-email", { email });
}

async function verifyChangeMail(token: string) {
  return post("/dao/verify-change-email", { token });
}

async function registerEmail(token: string, password: string) {
  let data;
  ui.showCaptcha(async (captcha_id: string, captcha_nonce: number) => {
    data = post("/dao/register", {
      token,
      password,
      captcha_id,
      captcha_nonce,
    });
  });
  return data;
}

async function getAuthInfos() {
  return post("/dao/get-auth", {});
}

async function unlinkWallet(identity: string, type: LoginType) {
  return post("/dao/unlink-wallet", { identity, type });
}

async function linkWallet({
  nonce,
  sign,
  type,
}: {
  nonce: string;
  sign: string;
  type: number;
}) {
  return post("/dao/link-wallet", { nonce, sign, type });
}

async function linkSocial({
  accessToken,
  type,
}: {
  accessToken: string;
  type: number;
}) {
  return post("/dao/link-wallet", { social_access_token: accessToken, type });
}

async function forgotPassword(email: string) {
  return post("/customer/forgot-password", { email });
}

async function resetPassword(token: string, password: string) {
  return post("/customer/verify-reset-password", { token, password });
}

async function deleteFarmerAccount(username: number) {
  return post("/dao/delete-farmer-account", { username });
}

async function changePassword(oldPassword: string, password: string) {
  return post("/dao/change-password", { oldPassword, password });
}

async function checkPassword(password: string) {
  return post("/dao/check-password", { password });
}

async function getQrCode2FA() {
  return get("/dao/2fa-get-qr-code");
}

async function getPublicConfig() {
  return get("/config/get-public-site");
}

async function confirmSetup2FA(otp: string, secret: string) {
  return post("/dao/2fa-setup-confirm", { otp, secret });
}

async function verifyOtp2FA(otp: string) {
  return post("/dao/2fa-verify-otp", { otp });
}

async function disable2FA() {
  return post("/dao/2fa-disabled", {});
}

async function sendRegisterEmail(email: string) {
  let data = post("/customer/send-register-email", { email });
  return data;
}

async function removeEmail() {
  return post("/dao/unlink-email", {});
}

async function linkEmail(token: string, password: string) {
  return post("/dao/link-email", { token, password });
}

async function requestLinkEmail(email: string) {
  return post("/dao/request-link-email", { email });
}

async function claimTokenRequest(tokenRequestKey: string, userId: string) {
  return post("/customer/claim-token-request-key", { tokenRequestKey, userId });
}

async function getMAGValue() {
  return post("/dao/get-mag-price", {});
}

async function getMSTRValue() {
  return post("/dao/get-mstr-price", {});
}

async function claimFaucet() {
  return post("/dao/claim-faucet", {});
}

async function getFaucetStatus() {
  return post("/dao/get-faucet-status", {});
}

async function editFarmer(
  identity: string,
  password: string,
  is2FARequired: boolean
) {
  return post("/dao/edit-farmer", { identity, password, is2FARequired });
}

async function createFarmerAccount(
  username: string,
  password: string,
  is2FARequired: boolean
) {
  return post("/dao/create-farmer-account", {
    username,
    password,
    is2FARequired,
  });
}

async function getDetailFarmer(username: string) {
  return post("/dao/get-farmer-analytics", { username });
}

async function getMAGFee(amount: number, chain: number) {
  return post("/convert/get-mag-fee-dao", { amount, chain });
}

async function getMSTRFee(amount: number, chain: number) {
  return post("/convert/get-mstr-fee-dao", { amount, chain });
}

async function getTransactions({
  offset,
  limit,
}: {
  offset: number;
  limit: number;
}) {
  return post("/dao/get-transaction-info", {
    offset,
    limit,
  });
}

async function getStakeTransactionsInfo({
  offset,
  limit,
}: {
  offset: number;
  limit: number;
}) {
  return post("/dao/get-stake-transaction-info", {
    offset,
    limit,
  });
}

async function setReferral(code: string) {
  return post("/customer/set-referral", { code });
}

async function invite(email: string) {
  return post("/dao/invite-by-referral", { email });
}

async function getReferralAnalysis({
  offset,
  limit,
}: {
  offset: number;
  limit: number;
}) {
  return post("/dao/get-referral-analytics", {
    offset,
    limit,
  });
}

async function getTicketPool() {
  return post("/dao/get-ticket-pool", {});
}

async function getCurrentEvent() {
  return post("/dao/get-current-event", {});
}

async function submitTicket(quantity: number, rarity: TicketRarity) {
  return post("/dao/submit-ticket", {
    quantity,
    rarity,
  });
}

async function transferTicket(quantity: number, rarity: TicketRarity) {
  const type = utils.getTicketType(rarity);
  return post("/dao/convert-item-nft-batch", {
    type,
    amount: quantity,
    chain: Chain.BSC,
  });
}

async function getUnstakeSignature(transaction_id: number) {
  return post("/dao/get-unstake-signature", {
    transaction_id,
  });
}

async function depositFT(
  amount: string,
  login_type?: LoginType,
  contractType?: ContractType
) {
  try {
    let value = login_type ? login_type : store.getState().logintype;
    await checkNetwork(value, "deposit ticket");
    // @ts-ignore
    let address = getContract(contractType, Chain[LoginType[value]]);
    switch (value) {
      case LoginType.Aura:
        const keplrAddress = await keplrService.connect(WALLET_PROVIDER.KEPLR);

        const auraAuthAddress =
          store.getState().wallets[value][AuthInterface.identity];

        if (
          auraAuthAddress?.toString().toLowerCase() !==
          keplrAddress?.toString().toLowerCase()
        ) {
          throw new Error("metamask_wallet_not_match");
        }
        await keplrService.execute(keplrAddress, address, {
          burn: {
            amount: amount.toString(),
          },
        });
        break;
      case LoginType.BSC:
      case LoginType.Avalanche:
      case LoginType.Okex:
        const account = await ethereum.request({
          method: "eth_requestAccounts",
        });
        let connectedWallet;
        if (store.getState().wallets[value]) {
          connectedWallet =
            store.getState().wallets[value][AuthInterface.identity];
        }
        if (
          connectedWallet?.toString().toLowerCase() !==
          account[0]?.toString().toLowerCase()
        ) {
          throw new Error("metamask_wallet_not_match");
        }
        if (!address) {
          throw new Error("contract_not_found");
        }
        FTContract.options.address = address;
        await FTContract.methods.burn(amount).send({ from: account[0] });
        break;
      default:
        throw new Error("only_in_bsc_chain");
    }
  } catch (error) {
    throw error;
  }
}

async function getFtSignature(amount: number, type: NFTType, chain: Chain) {
  console.log("here", { chain });
  // @ts-ignore
  let loginType = LoginType[Chain[chain]];
  switch (chain) {
    case Chain.BSC:
    case Chain.Avalanche:
    case Chain.Okex:
      await checkNetwork(loginType, "get ft signature");
      const account = await ethereum.request({
        method: "eth_requestAccounts",
      });
      let connectedWallet;
      if (store.getState().wallets[loginType]) {
        connectedWallet =
          store.getState().wallets[loginType][AuthInterface.identity];
      }
      if (
        connectedWallet?.toString().toLowerCase() !==
        account[0]?.toString().toLowerCase()
      ) {
        throw new Error("metamask_wallet_not_match");
      }
      break;
    case Chain.Aura:
      const keplrAddress = await keplrService.connect(WALLET_PROVIDER.KEPLR);
      let auraConnectedWallet;
      if (store.getState().wallets[loginType]) {
        auraConnectedWallet =
          store.getState().wallets[loginType][AuthInterface.identity];
      }
      if (
        auraConnectedWallet?.toString().toLowerCase() !==
        keplrAddress?.toString().toLowerCase()
      ) {
        throw new Error("metamask_wallet_not_match");
      }
      break;
  }

  return post("/dao/get-ft-signature", {
    amount,
    type,
    chain,
  });
}

async function cancelTransaction(transaction_id: number) {
  return post("/dao/cancel-transaction", {
    transaction_id,
  });
}

async function claimRefereeCashback(referee_id: number) {
  return post("/dao/claim-referee-cashback", {
    referee_id,
  });
}

async function claimAllRefereeCashback() {
  return post("/dao/claim-all-cashback", {});
}

async function getSSOMeta() {
  if (!sessionStorage.getItem("reload_metamask")) {
    console.log("reload");
    sessionStorage.setItem("reload_metamask", "true");
    // window.location.reload();
  }
  const ssoMeta = await get("/customer/get-SSO-meta");
  store.dispatch(clearSSOMeta());
  store.dispatch(setSSOMeta(ssoMeta));
}

async function getCaptcha(id: string) {
  return getImage(`/captcha/get-image?id=${id}`);
}
async function verifyCaptcha({ id, nonce }: { id: string; nonce: number }) {
  return post(`/captcha/test-captcha`, {
    id,
    nonce,
  });
}
async function registerWithEmailToken(token: string) {
  await api.post("/dao/register-by-email-pw", {
    token,
  });
}
async function linkWithEmailToken(token: string) {
  await api.post("/dao/link-by-email-pw", {
    token,
  });
}
async function getFtCleanFee(type: NFTType, amount: number) {
  return await api.post("/dao/get-ft-clean-fee", {
    type,
    amount,
  });
}
async function getItemsOnchain(plotIds: number[], mongenIds: number[]) {
  return await api.post("/click2earn/get-items-onchain", {
    plotIds,
    mongenIds,
  });
}

const checkSameAccount = async (auth_address: string) => {
  const account = await ethereum.request({
    method: "eth_requestAccounts",
  });
  if (
    auth_address?.toString().toLowerCase() !==
    account[0]?.toString().toLowerCase()
  ) {
    throw new Error("metamask_wallet_not_match");
  }
};

const getInventory = async (rarity?: Rarity) => {
  return await post("/dao/get-items", { rarity });
};

const getVault = async () => {
  return await post("/dao/get-customer-dao-vault-mag", {});
};

const exchangePoint = async ({
  mongen_ids,
  plot_ids,
  rarity,
}: {
  mongen_ids: number[];
  plot_ids: number[];
  rarity: Rarity;
}) => {
  return await post("/dao/buy-dao-stake-point", {
    mongen_ids,
    plot_ids,
    rarity,
  });
};

const restakeMstrStake = async (stake_in_game: boolean) => {
  return await post("/dao/claim-mstr-stake", {
    stake_in_game,
  });
};

const restakeMagStake = async (type?: Rarity) => {
  return await post("/dao/claim-mag-stake", {
    type,
  });
};

const claimMstrStake = async (stake_in_game: boolean, logintype: LoginType) => {
  return await post("/dao/withdraw-mstr-stake", {
    stake_in_game,
    logintype,
  });
};

const claimMagStake = async (logintype: LoginType, type?: Rarity) => {
  return await post("/dao/withdraw-mag-stake", {
    logintype,
    type,
  });
};

const unstakePeriodMstr = async (id: number, logintype: LoginType) => {
  return await post("/dao/unstake-mstr-time", {
    id,
    logintype,
  });
};

const getConfig = async () => {
  return await post("/dao/get-config", {});
};

const stakeMSTRPeriodIngame = async (
  quantity: number,
  duration: MstrSharedRevenueTime
) => {
  return await post("/dao/stake-mstr-time-from-in-game", {
    quantity,
    duration,
  });
};

const restakeMstrTime = async (duration: MstrSharedRevenueTime) => {
  return await post("/dao/restake-mstr-time", {
    duration,
  });
};

const getAprReport = async ({ groupBy }: { groupBy: "hour" | "date" }) => {
  let limit = groupBy === "hour" ? 24 : 7;
  return await post("/dao/get-dao-apr-report", {
    type: groupBy,
    limit,
  });
};

const withdrawUSDT = async (amount: number, login_type: LoginType) => {
  return await api.post("/dao/withdraw-usdt", {
    amount,
    logintype: login_type,
  });
};

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
// export
const api = {
  restakeMstrTime,
  stakeMSTRPeriodIngame,
  getConfig,
  restakeMstrStake,
  restakeMagStake,
  unstakePeriodMstr,
  claimMstrStake,
  claimMagStake,
  checkSameAccount,
  getItemsOnchain,
  getFtCleanFee,
  registerWithEmailToken,
  verifyCaptcha,
  getCaptcha,
  claimRefereeCashback,
  claimAllRefereeCashback,
  getFtSignature,
  cancelTransaction,
  post,
  postFormData,
  uploadImage,
  login,
  loginTerra,
  loginWith2FA,
  loginByPassword,
  registerMetaMask,
  registerTerra,
  registerEmail,
  unlinkWallet,
  linkWallet,
  linkSocial,
  invite,
  setReferral,
  claimFaucet,
  claimTokenRequest,
  setStateLogin,
  requestLinkEmail,
  requestChangeMail,
  verifyChangeMail,
  linkEmail,
  sendRegisterEmail,
  removeEmail,
  disable2FA,
  verifyOtp2FA,
  confirmSetup2FA,
  checkPassword,
  changePassword,
  resetPassword,
  forgotPassword,
  createFarmerAccount,
  editFarmer,
  deleteFarmerAccount,
  transferTicket,
  submitTicket,
  getNonce,
  getQrCode2FA,
  getMessages,
  getDetailFarmer,
  getTransactions,
  getPublicConfig,
  getCurrentEvent,
  getTicketPool,
  getReferralAnalysis,
  getMetamaskSign,
  getMSTRBalance,
  getMAGBalance,
  getAuthInfos,
  getMainBalanceChain,
  getMAGFee,
  getMSTRFee,
  getTokenBalance,
  getFaucetStatus,
  getUnstakeSignature,
  getMAGValue,
  getMSTRValue,
  depositMSTR,
  depositMAG,
  depositTicket,
  updateProfile,
  requestWithdraw,
  getSSOMeta,
  checkNetwork,
  registerBySocial,
  loginBySocial,
  checkTrueWallet,
  depositFT,
  GetNfts,
  getInventory,
  getVault,
  exchangePoint,
  getStakeTransactionsInfo,
  depositPeriodMSTR,
  getAprReport,
  depositUSDT,
  withdrawUSDT,
  getUSDTBalance,
  linkWithEmailToken,
  loginCosmos,
  registerCosmos,
  getKeplrSign,
  getTicketsBalance
};
export default api;
